/**
 * The English dictionary, and — because `Dictionary` is `typeof en` — the
 * authoritative key set for every locale.
 *
 * Adding a key here without adding it to `./ko` is a typecheck failure, not a
 * silent fallback to English at runtime. That is deliberate: this app's copy
 * carries its epistemic discipline, and a screen that quietly reverts one
 * sentence to English is a screen where the Korean reader loses the hedge.
 *
 * Do NOT annotate this object `as const`. The literal types would then be
 * narrower than the strings `ko` supplies, and nothing would typecheck.
 *
 * Entries that interpolate are functions rather than templates with `{0}`
 * placeholders, so Korean can reorder, drop or merge the arguments — Korean
 * word order and English word order rarely survive the same template.
 *
 * Terms deliberately left untranslated in every locale: Bear Trap, Rally,
 * Alliance Championship, Swordland, Fortress, Sanctuary, Castle, Viking
 * Vengeance, KvK, R4/R5, TC. Korean players use these as-is; translating them
 * makes the UI harder to use, not easier.
 */

export const en = {
  meta: {
    /** Shown on the language toggle, in its own language. */
    localeName: 'English',
    htmlLang: 'en',
    /** Passed to `Intl`. */
    intlLocale: 'en-US',
  },

  // ---------------------------------------------------------------------------
  // Words that recur across many screens.
  // ---------------------------------------------------------------------------
  common: {
    unknown: 'unknown',
    notRecorded: 'not recorded',
    notRecordedTitle: 'Not recorded',
    notEntered: 'not entered',
    none: 'none',
    dash: '—',
    unnamedPlayer: 'unnamed player',
    unnamedAlliance: 'unnamed alliance',
    unnamedLead: 'unnamed lead',
    untitled: 'untitled',
    notIdentified: 'not identified',
    noAlliance: 'no alliance',
    notSynced: 'Not synced',
    /**
     * "We refuse to compute a trend", never "the trend was zero". A value of 0
     * is a measurement; this is the absence of one.
     */
    notEnoughHistory: 'Not enough history',
    notScored: 'not scored',
    notClassified: 'not classified',
    notCalculable: 'not calculable',
    notRanked: 'Not ranked',
    observed: 'observed',
    player: 'Player',
    alliance: 'Alliance',
    power: 'Power',
    kills: 'Kills',
    level: 'Level',
    vip: 'VIP',
    tc: 'TC',
    townCentreLevel: 'Town centre level',
    members: 'Members',
    wave: 'Wave',
    confidence: 'Confidence',
    samples: 'Samples',
    why: 'Why',
    close: 'Close',
    hide: 'Hide',
    open: 'Open',
    cancel: 'Cancel',
    saving: 'Saving…',
    discard: 'Discard',
    or: 'or',
    tryAgain: 'Try again',
    selectAll: 'Select all',
    clear: 'Clear',
    selectPlaceholder: 'Select…',
    optional: 'optional',
    openSyncStatus: 'Open Sync Status',
    noCoreAllianceTitle: 'No core alliance configured',
    loadingAlliance: 'Loading alliance',
    loadingRoster: 'Loading roster',
    loadingState: 'Loading state',
    nothingToShow: 'Nothing to show.',
    days: (days: number) => `${days}d`,
  },

  duration: {
    seconds: (seconds: number) => `${seconds}s`,
    minutes: (minutes: number) => `${minutes}m`,
    minutesSeconds: (minutes: number, seconds: number) => `${minutes}m ${seconds}s`,
    hoursMinutes: (hours: number, minutes: number) => `${hours}h ${minutes}m`,
  },

  /**
   * A percentile position. English uses the ordinal form computed by
   * `format.ts`; Korean has no ordinal suffix and names the scale instead, so
   * the value is passed alongside.
   */
  percentile: (value: number, ordinalForm: string) => ordinalForm,

  /**
   * Dashboard warnings (spec section 58).
   *
   * The server sends a `code` plus `params` alongside its English `message`,
   * so these sentences can be rebuilt in the operator's language instead of
   * arriving pre-formatted. Anything this table does not cover — including the
   * server's own `UNKNOWN` — renders `message` verbatim rather than blank.
   *
   * Note these are NOT simply the server's English strings copied across. The
   * server writes "50 sync run(s)", which is a template artefact; the frontend
   * owns the rendering now, so it says "50 sync runs".
   */
  dashboardWarnings: {
    NO_CORE_ALLIANCE:
      'No core alliance is configured. Set CORE_ALLIANCE_ID in settings to enable alliance views.',
    CORE_ALLIANCE_NEVER_SYNCED: (allianceId: string) =>
      `Core alliance ${allianceId} is configured but has not been synced yet.`,
    /** @param age A pre-formatted compact label from the server, e.g. "54m". */
    CORE_ROSTER_STALE: (age: string) => `The core alliance roster is ${age} old.`,
    API_BUDGET: (used: string, budget: string) =>
      `Jeabs daily request estimate is ${used} of ${budget}. Syncs will pause when the budget is spent.`,
    SYNC_FAILURES: (failures: number) =>
      `${failures} sync run${failures === 1 ? '' : 's'} failed in the last 6 hours. See the sync status screen for detail.`,
  },

  // ---------------------------------------------------------------------------
  // Server enums. Unknown members fall back to the raw humanized id rather than
  // a guessed translation — the same rule the app applies to unknown hero ids.
  // ---------------------------------------------------------------------------
  enums: {
    detectedEvent: {
      PLAYER_JOINED_ALLIANCE: 'Player joined alliance',
      PLAYER_LEFT_ALLIANCE: 'Player left alliance',
      PLAYER_TRANSFERRED_ALLIANCE: 'Player transferred alliance',
      PLAYER_RENAMED: 'Player renamed',
      TC_UPGRADE: 'TC upgrade',
      POWER_SPIKE: 'Power spike',
      PLAYER_DORMANT: 'Player dormant',
      PLAYER_REACTIVATED: 'Player reactivated',
    },
    eventType: {
      BEAR_TRAP: 'Bear Trap',
      ALLIANCE_CHAMPIONSHIP: 'Alliance Championship',
      SWORDLAND: 'Swordland',
      SANCTUARY: 'Sanctuary',
      FORTRESS: 'Fortress',
      CASTLE: 'Castle',
      VIKING_VENGEANCE: 'Viking Vengeance',
      TRI_ALLIANCE_CLASH: 'Tri-Alliance Clash',
      ALLIANCE_MOBILIZATION: 'Alliance Mobilization',
      ALLIANCE_BRAWL: 'Alliance Brawl',
      KVK: 'KvK',
      CUSTOM: 'Custom',
    },
    eventStatus: {
      PLANNED: 'Planned',
      OPEN: 'Open',
      IN_PROGRESS: 'In progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      /** The dashboard types status as an open string and does emit this. */
      DRAFT: 'Draft',
    },
    /**
     * Spec section 50: this data exists so an operation can be staffed, never
     * so members can be ranked. No entry here may read as an accusation.
     */
    attendanceStatus: {
      REGISTERED: 'Registered',
      CONFIRMED: 'Confirmed',
      ATTENDED: 'Attended',
      LATE: 'Joined late',
      NO_SHOW: 'Did not take part',
      COMPLETED_ROLE: 'Completed role',
      EXCUSED: 'Excused in advance',
    },
    growthFlag: {
      WHALE: 'Whale',
      FAST_GROWER: 'Fast grower',
      RISING: 'Rising',
      NORMAL: 'Normal',
      DORMANT: 'Dormant',
    },
    rallyRole: {
      BEAR_OFFENSE: 'Bear offense',
      PVP_ATTACK: 'PvP attack',
      PVP_GARRISON: 'PvP garrison',
      PVP_JOINER: 'PvP joiner',
      CASTLE_ATTACK: 'Castle attack',
      CASTLE_GARRISON: 'Castle garrison',
      FORTRESS_ATTACK: 'Fortress attack',
      FORTRESS_GARRISON: 'Fortress garrison',
      SANCTUARY_ATTACK: 'Sanctuary attack',
      SANCTUARY_GARRISON: 'Sanctuary garrison',
    },
  },

  // ---------------------------------------------------------------------------
  // App shell
  // ---------------------------------------------------------------------------
  shell: {
    brand: 'Alliance Ops',
    brandSub: 'Kingshot',
    navLabel: 'Main',
    devBanner:
      'Development fixtures active — every number on screen is synthetic. Not connected to the alliance API.',
    signedIn: 'Signed in',
    noLinkedPlayer: 'no linked player',
    signOut: 'Sign out',
    languageLabel: 'Language',
    navToggle: 'Open navigation',
    navClose: 'Close navigation',
    themeLabel: 'Theme',
    theme: {
      system: 'System',
      light: 'Light',
      dark: 'Dark',
    },
    restoringSession: 'Restoring your session',
  },

  nav: {
    groups: {
      alliance: 'Alliance',
      bear: 'Bear Trap',
      events: 'Events',
      stateIntel: 'State Intel',
      data: 'Data',
    },
    overview: 'Overview',
    members: 'Members',
    growth: 'Growth',
    activity: 'Activity',
    roles: 'Roles',
    personalAnalyzer: 'Personal Analyzer',
    alliancePlanner: 'Alliance Planner',
    bearHistory: 'History',
    experiments: 'Experiments',
    rallyLab: 'Rally Lab',
    championship: 'Championship',
    swordland: 'Swordland',
    fortress: 'Sanctuary / Fortress',
    castle: 'Castle',
    viking: 'Viking',
    triAlliance: 'Tri-Alliance',
    mobilization: 'Mobilization / Brawl',
    stateAlliances: 'Alliances',
    statePlayers: 'Players',
    transfers: 'Transfers',
    leaderboards: 'Leaderboards',
    kvk: 'KvK',
    freshness: 'Freshness',
    rules: 'Rules',
    catalog: 'Catalog',
    syncStatus: 'Sync Status',
    settings: 'Settings',
  },

  auth: {
    title: 'Alliance Ops',
    blurb: 'Private alliance tooling. Sign in with the account your R5 issued.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signInFailed: 'Sign-in failed.',
  },

  // ---------------------------------------------------------------------------
  // Loading / empty / error states
  // ---------------------------------------------------------------------------
  states: {
    loading: 'Loading',
    noDataTitle: 'No data',
    noDataBody: 'The API returned nothing for this view.',
    notAvailableYetTitle: (feature: string) => `${feature} — not available yet`,
    notAvailableYetBody: (phase: string) =>
      `The data source for this screen lands in ${phase}. Nothing is shown here until it does — these numbers would have to be invented, and an invented number is worse than a blank one.`,
    missingRouteTitle: 'This data source is not live yet',
    missingRouteBody:
      'The API responded that this route does not exist. That is expected while the backend is still being rolled out; nothing is wrong with your account.',
    genericErrorTitle: 'Something went wrong',
    unexpectedError: 'An unexpected error occurred.',
    errorTitles: {
      UNAUTHENTICATED: 'Signed out',
      FORBIDDEN: 'Not permitted',
      RATE_LIMIT: 'Upstream rate limit reached',
      /**
       * The response no longer matches the shared contract. Named precisely,
       * because "something went wrong" would hide the one failure mode that
       * makes a dashboard render numbers it cannot justify.
       */
      CONTRACT_MISMATCH: 'The API and this app disagree on the response shape',
      NETWORK: 'Cannot reach the API',
      REQUEST_FAILED: 'Request failed',
    },
  },

  api: {
    sessionExpired: 'Your session has expired. Sign in again.',
    unreachable:
      'Could not reach the alliance API. Check your connection and that the Worker is running.',
    notJson: 'The API returned a response that was not JSON.',
    contractMismatch: (path: string) =>
      `The API response for ${path} did not match the shared schema.`,
    forbidden: 'This account does not have access to that.',
    notFound: 'That resource does not exist yet.',
    httpStatus: (status: number) => `The API returned HTTP ${status}.`,
    bearAnalysisContractMismatch: (playerId: string) =>
      `The Bear analysis for player ${playerId} did not match the shared schema.`,
  },

  config: {
    missing: (name: string) =>
      `Missing ${name}. Copy apps/web/.env.example to apps/web/.env and fill it in.`,
    notConfigured: 'The app is not configured.',
  },

  // ---------------------------------------------------------------------------
  // Per-field data quality (spec section 56)
  // ---------------------------------------------------------------------------
  quality: {
    confirmed: (field: string) => `${field}: observed directly from a fresh upstream response.`,
    possible: (field: string) =>
      `${field}: reported by the upstream API, but it returns the same value for "not enriched yet". It may not be a real measurement.`,
    unknown: (field: string) =>
      `${field}: the upstream meaning of this value is not established. Do not act on it.`,
    stale: (field: string) =>
      `${field}: a real measurement, but old enough that it may no longer hold.`,
    neverRecorded: (field: string) => `${field} has never been recorded.`,
    flagAria: (quality: string) => `data quality: ${quality.toLowerCase()}`,
    noneReported: 'No per-field quality reported for this record.',
    allConfirmed: (total: number) => `All ${total} fields confirmed.`,
    unconfirmedCount: (unconfirmed: number, total: number) =>
      `${unconfirmed} of ${total} fields unconfirmed`,
  },

  // ---------------------------------------------------------------------------
  // Freshness (spec section 64). Each upstream clock stays separately labelled;
  // there is deliberately no combined "last updated" anywhere.
  // ---------------------------------------------------------------------------
  freshness: {
    sources: {
      profile: 'Profile',
      kills: 'Kills',
      standings: 'Standings',
      heroGear: 'Hero Gear',
      allianceRoster: 'Alliance Roster',
      alliance: 'Alliance',
      kingdom: 'Kingdom',
    },
    stripLabel: 'Data freshness by source',
    never: 'never',
    buckets: {
      FRESH: 'within this source’s normal refresh interval',
      RECENT: 'slightly behind its normal refresh interval',
      AGING: 'noticeably behind; treat derived numbers with care',
      STALE: 'too old to rely on for a decision',
      UNKNOWN: 'never successfully fetched',
    },
    badgeTitle: (label: string, age: string, explanation: string) =>
      `${label}: ${age} old — ${explanation}`,
  },

  // ---------------------------------------------------------------------------
  // Inference rendering (spec sections 1.1, 18, 28)
  // ---------------------------------------------------------------------------
  inference: {
    confidenceTitleWith: (percent: string) =>
      `Model confidence ${percent}. This is a calculated value, not an observation.`,
    confidenceTitle: 'This is a calculated value, not an observation.',
    /** Heading suffix marking a block as inference rather than fact. */
    calculated: 'calculated',
    calculatedTitleWith: (modelVersion: string) =>
      `Calculated by this system (${modelVersion}), not reported by the game.`,
    calculatedTitle: 'Calculated by this system, not reported by the game.',
    nouns: {
      samples: 'samples',
      snapshots: 'snapshots',
      sessions: 'sessions',
      rallies: 'rallies',
      recordedRallies: 'recorded rallies',
      observedRallies: 'observed rallies',
      leads: 'leads',
      joins: 'joins',
      observations: 'observations',
    },
    noSamples: (noun: string) => `no ${noun}`,
    noSamplesTitle: (noun: string) => `No ${noun} recorded, so there is nothing to average.`,
    lowSamples: (samples: number, noun: string) => `${samples} ${noun} · low`,
    lowSamplesTitle: (samples: number, noun: string, threshold: number) =>
      `Only ${samples} ${noun}. Below ${threshold} the spread is usually wider than the difference you are looking at — read this as a hint, not a measurement.`,
    samples: (samples: number, noun: string) => `${samples} ${noun}`,
    samplesTitle: (samples: number, noun: string) => `${samples} ${noun} behind this figure.`,
    noComponents:
      'This score did not record its components, so it cannot be explained. Treat it as unverified.',
    whyThisScore: 'Why this score',
    noReasons: 'The model did not record why it produced this result.',
  },

  // ---------------------------------------------------------------------------
  // Overview (spec section 58)
  // ---------------------------------------------------------------------------
  overview: {
    title: 'Overview',
    subtitle:
      'Current state of the core alliance, and what needs attention before the next operation.',
    loading: 'Loading overview',
    noCoreAllianceBody:
      'Nothing has been synced yet. An R5 needs to set the kingdom and core alliance in server-side configuration and run the first sync; this screen fills in from that point onward.',
    alliancePower: 'Alliance Power',
    stateRank: 'State Rank',
    stateRankFoot: 'by alliance power in kingdom',
    stateRankAbsent: 'Not ranked yet',
    stateRankValue: (rank: number) => `#${rank}`,
    memberCount: (count: string, max: string | null) => (max ? `${count} / ${max}` : count),
    growth24h: '24h Growth',
    growth7d: '7d Growth',
    moversTitle: 'Movers',
    moversEmpty:
      'No member has changed enough between snapshots to register yet. This fills in once there are at least two snapshots a day apart.',
    recentActivityTitle: 'Recent Activity',
    recentActivityEmpty:
      'Nothing detected yet. Joins, departures, renames and TC upgrades appear here once two consecutive syncs can be compared.',
    detectedBadge: 'detected',
    detectedTitle:
      'Detected by comparing snapshots; a missed sync can produce a false positive.',
    upcomingTitle: 'Upcoming Operations',
    upcomingEmpty:
      'No operations scheduled. Events are created by an R4 and are not inferred from the game.',
    columns: {
      operation: 'Operation',
      name: 'Name',
      starts: 'Starts',
      status: 'Status',
    },
    noStartTime: 'no start time',
  },

  // ---------------------------------------------------------------------------
  // Alliance section
  // ---------------------------------------------------------------------------
  alliance: {
    noCoreAllianceBody:
      'The kingdom and core alliance are set server-side and must be synced at least once before these screens have anything to show.',

    members: {
      title: 'Members',
      subtitle: (allianceLabel: string) =>
        `Roster of ${allianceLabel}. A dashed underline marks a value the upstream API reports but cannot confirm — most often a zero that means "not enriched".`,
      searchPlaceholder: 'Search members',
      searchAria: 'Search members',
      emptyTitle: 'No members synced',
      emptyBody:
        'The alliance roster has never been fetched successfully. Check Sync Status for recent failures.',
      noSearchMatch: 'No member matches that search.',
      /**
       * "Identified" and "reported" are two different counts and must never
       * collapse into one. Jeabs returns unidentified members as blank rows, so
       * the gap is a hole in *our view*, not people who left the alliance.
       */
      identified: (identified: number) =>
        `${identified} member${identified === 1 ? '' : 's'} identified in the last roster snapshot.`,
      identifiedOf: (identified: number, reported: number) =>
        `${identified} member${identified === 1 ? '' : 's'} identified in the last roster snapshot of ${reported} reported by the game.`,
      couldNotIdentify: (missing: number) => `${missing} could not be identified`,
      /**
       * Deliberately says the gap CLOSES. The upstream enriches a rotating
       * subset of the roster on each read, so identified membership is a union
       * that accumulates across syncs -- observed going from 46 to 55 in a
       * single roster fetch. Copy that reads as a permanent ceiling would have
       * an operator conclude half the alliance is permanently invisible.
       */
      couldNotIdentifyWhy:
        '— the data source returned them without a name or ID on this read. They have not left the alliance. It enriches a different subset each time, so this list fills in as syncing continues.',
    },

    growth: {
      title: 'Growth',
      subtitle: (allianceLabel: string) => `Power and membership trend for ${allianceLabel}.`,
      loading: 'Loading growth',
      cardTitle: 'Growth',
      window24h: '24h',
      window7d: '7d',
      window30d: '30d',
      memberChange7d: 'Member change 7d',
      thinHistory: (snapshots: number) =>
        `Computed from ${snapshots} snapshot${snapshots === 1 ? '' : 's'}. A rate needs more history than that before it describes a trend rather than a moment.`,
      emptyTitle: 'No growth analysis yet',
      emptyBody:
        'Alliance growth is computed from consecutive snapshots. Once there are at least two, the deltas appear here.',
    },

    activity: {
      title: 'Activity',
      subtitle:
        'Joins, departures, renames and upgrades detected by comparing consecutive snapshots.',
      loading: 'Loading activity',
      emptyTitle: 'Nothing detected yet',
      emptyBody:
        'Detection compares one snapshot against the previous one, so the first entries appear after the second successful sync.',
      caveat:
        'These are inferences drawn from snapshot differences, not notifications from the game. A missed sync can make one movement look like two.',
      columns: {
        detected: 'Detected',
        event: 'Event',
        player: 'Player',
        alliance: 'Alliance',
        relatedAlliance: 'Related alliance',
        confidence: 'Confidence',
      },
    },

    roles: {
      title: 'Roles',
      subtitle:
        'Who is assigned to what, and the manual decisions that override the recommendation engine.',
      feature: 'Role assignment',
      phase: 'the phase that delivers manual operational overrides (spec section 51)',
      describes:
        'This screen will hold standing decisions — force-lead, do-not-lead, prefer-garrison, unavailable — that take priority over every score in the app. Until the overrides API exists, those decisions can still be made per-plan in the Bear Planner, where they are sent as constraints.',
    },
  },

  // ---------------------------------------------------------------------------
  // Player detail
  // ---------------------------------------------------------------------------
  player: {
    loading: 'Loading player',
    tabs: {
      growth: 'Growth',
      heroes: 'Heroes',
      capabilities: 'Capabilities',
      bear: 'Bear',
      events: 'Event History',
    },
    columns: {
      profileAge: 'Profile age',
    },
  },

  growth: {
    metricsEmptyTitle: 'No growth analysis yet',
    metricsEmptyBody:
      'Growth is computed from at least two snapshots. Once this player has been synced more than once, 24h / 7d / 30d figures appear here with their sample count.',
    cardTitle: 'Growth',
    percentileAlliance: 'Percentile in alliance',
    percentileKingdom: 'Percentile in kingdom',
    growthFlag: 'Growth flag',
    growthFlagTitle: 'A classification this system assigned, not something the game reports.',
    historyTitle: 'Power history',
    historyLoading: 'Loading history',
    historyEmptyTitle: 'No snapshots in this range',
    historyEmptyBody:
      "Snapshots are only stored when a player's data actually changes, so a quiet account can legitimately have none. Widen the range, or check Sync Status if you expected activity.",
    snapshotLogTitle: 'Snapshot log',
    snapshotLogEmpty: 'Nothing recorded in this range.',
    notEnoughPointsTitle: 'Not enough points to draw a line',
    notEnoughPointsBody: (points: number) =>
      `Only ${points} snapshot${points === 1 ? '' : 's'} in this range ${points === 1 ? 'has' : 'have'} a recorded power value. A trend needs at least two.`,
    omittedPoints: (omitted: number) =>
      `${omitted} snapshot${omitted === 1 ? '' : 's'} in this range had no recorded power and ${omitted === 1 ? 'is' : 'are'} omitted from the line rather than drawn as zero.`,
    columns: {
      captured: 'Captured',
    },
  },

  heroes: {
    loading: 'Loading heroes',
    emptyTitle: 'No hero data synced',
    emptyBody:
      'Hero and gear data comes from a separate upstream request on its own schedule. If this player has never had one succeed, nothing is stored yet.',
    noHeroesTitle: 'No heroes in the latest snapshot',
    noHeroesBody: 'The snapshot exists but contains no hero entries.',
    rosterBadge: (completeness: string) => `roster: ${completeness}`,
    rosterCompleteness: {
      UNVERIFIED: 'unverified',
      ASSUMED_COMPLETE: 'assumed complete',
      KNOWN_PARTIAL: 'known partial',
    },
    rosterNote: {
      UNVERIFIED:
        'The upstream hero list is not known to be a complete roster. Heroes this player owns may be missing from it, so absence here is not evidence of absence in game.',
      ASSUMED_COMPLETE:
        'This list is assumed complete, but that assumption has not been verified against the game client.',
      KNOWN_PARTIAL:
        'This list is known to be partial. Treat it as the heroes we can see, not the heroes they have.',
    },
    rawHeroId: (heroId: number | string) => `hero #${heroId}`,
    rawHeroIdTitle:
      'This hero id is not in the catalog, so we show the raw id rather than guessing a name.',
    unverifiedId: 'unverified id',
    unverifiedIdTitle: 'Not present in hero_catalog, or present but unverified.',
    unverified: 'unverified',
    unverifiedEquipmentTitle: 'Not in equipment_catalog.',
    level: 'Level',
    notReported: 'not reported',
    /**
     * Jeabs calls this field `star`. The encoding is unverified, so it is
     * reported as a stage number and never drawn as a row of stars.
     */
    starStage: 'Star stage',
    starStageTitle:
      'Jeabs calls this field `star`. Its encoding is not verified, so it is reported as a stage number and never drawn as a count of stars.',
    slotPosition: 'Slot / position',
    exclusiveEquipment: 'Exclusive equipment',
    equipLevel: (level: number) => ` · lvl ${level}`,
    noneRecorded: 'none recorded',
    skills: 'Skills',
    noSkills: 'No skill entries in this snapshot.',
    rawSkillId: (skillId: number | string) => `skill #${skillId}`,
    skillLevel: (level: number) => `lvl ${level}`,
    skillLevelUnknown: 'level unknown',
    skillOrderNote:
      'Order is display order only; it carries no verified meaning about skill priority.',
    equipmentBySlot: 'Equipment by slot',
    noEquipment: 'No equipment entries in this snapshot.',
    equipmentColumns: {
      slotId: 'Slot id',
      slotIdTitle:
        'The upstream `sid`. Slot identity comes from this value, never from the order items arrive in.',
      equipment: 'Equipment',
      enhance: 'Enhance',
      refine: 'Refine',
    },
    opaqueTitle: 'Raw upstream stats',
    opaqueBlurb:
      'These arrive from Jeabs as numbered keys with no documented meaning. They are shown exactly as received. Where the catalog holds a guess at what a key represents, it is labelled a candidate with its confidence — a candidate is not a name, and nothing in this app computes from an unverified key.',
    opaqueEmpty: 'No stat keys in the latest snapshot.',
    opaqueColumns: {
      key: 'Key',
      value: 'Value',
      candidateMeaning: 'Candidate meaning',
      candidateConfidence: 'Candidate confidence',
    },
    verified: 'verified',
    candidateOnly: 'candidate only',
    noCandidate: 'no candidate',
  },

  capabilities: {
    loading: 'Loading capabilities',
    emptyTitle: 'No capability scores yet',
    emptyBody:
      'Capability scores are recalculated from recorded operations and player state. Until this alliance has run and recorded some operations, there is nothing to score from — and a score built from nothing would just be power in disguise.',
    blurb: '— every figure below is calculated by this system, not reported by the game.',
    computedFrom: (when: string) => ` Computed from data as of ${when}.`,
    whatThisScoreIsMadeOf: 'What this score is made of',
  },

  eventHistory: {
    /** Spec section 50: staffing, never ranking. */
    notice:
      'Attendance is recorded so operations can be staffed, not so members can be ranked. Absence from this list means nothing has been recorded — it is not a statement about the player.',
    bearTitle: 'Bear Trap',
    bearBody: 'Bear participation and per-rally results are on the',
    bearTabName: 'Bear',
    bearBodyTail: 'tab, which is built from the same recorded sessions.',
    otherTitle: 'Other operations',
    otherBody:
      "No results have been recorded for these yet, because the screens that record them are not built. Once they are, each operation appears here with the player's role and contribution.",
    seeEvents: 'See',
    seeEventsLink: 'Events',
    seeEventsTail: 'for what each of those will cover.',
  },

  // ---------------------------------------------------------------------------
  // Personal Bear Analyzer (spec sections 26, 27, 60)
  // ---------------------------------------------------------------------------
  bearAnalysis: {
    myTitle: 'My Bear analysis',
    playerTitle: 'Bear analysis',
    loading: 'Loading Bear analysis',
    resolvingAccount: 'Resolving your account',
    openFullPage: 'Open as a full page',
    openPlayerDetail: 'Open full player detail',
    openPlayerDetailInline: 'open full player detail',
    noPlayerTitle: 'No player selected',
    noPlayerBody: 'This route needs a player id.',
    notLinkedTitle: 'Your account is not linked to a player yet',
    notLinkedBody:
      'This page analyses your own recorded Bear rallies, so it needs to know which in-game account is yours. Linking is a manual step: an R5 sets your player on your workspace account. Nothing is wrong, and nothing is missing from the data.',
    meantimeTitle: 'In the meantime',
    meantimeBody: 'The same analysis is available for anyone with recorded rallies.',
    browseMembers: 'Browse members',
    openAnalysisFor: 'Open Bear analysis for',
    selectPlayer: 'Select a player…',

    /**
     * Used only when the server sent `empty: true` with no message of its own.
     * It states that nothing was recorded — never that the analysis returned
     * zero, which would be a measurement.
     */
    noParticipations: 'No Bear participations have been recorded for this player yet.',
    emptyTitle: 'No Bear sessions recorded for this player',
    emptyBody:
      'This analysis is built entirely from rallies an R4 has entered after a session. Nothing is inferred from power or gear alone, because gear predicts Bear damage poorly and a number invented from it would look exactly as confident as a real one.',

    recommendationTitle: 'Current recommendation',
    /**
     * A recommendation, not a verdict. Each of these stays hedged; none of them
     * asserts what the player *is*.
     */
    roleHeadline: {
      LEAD: 'Leading looks like the stronger role',
      JOIN: 'Joining looks like the stronger role',
      MIXED: 'Both, depending on the wave',
      INSUFFICIENT_DATA: 'Not enough recorded sessions to say',
    },
    leadAverage: 'Lead average',
    neverLed: 'Never led',
    joinAverage: 'Join average',
    neverJoined: 'Never joined',
    leadVsJoin: 'Lead vs join',
    leadVsJoinFoot: 'positive favours leading',
    notComparable: 'Not comparable',
    returnCycle: 'Rally return cycle',
    returnCycleFoot: 'observed median',
    insufficientNotice:
      'The recorded history is too thin to prefer one role over the other. Record a few more sessions in both roles before planning around this.',
    computedFrom: (when: string) => `Computed from data as of ${when}.`,
    unrecordedTime: 'an unrecorded time',

    leadPerformance: 'Lead performance',
    joinPerformance: 'Join performance',
    noRalliesInRole: 'No rallies recorded in this role.',
    averageDamage: 'Average damage',
    medianDamage: 'Median damage',
    bestRecorded: 'Best recorded',
    damagePerTroop: 'Damage per troop',
    damagePerTroopTitle:
      'Only computed over rallies where troop counts were actually entered. Rallies with blank troop counts are excluded rather than counted as zero.',
    noTroopCounts: 'no troop counts recorded',
    thinRoleSamples: (samples: number) =>
      `${samples} rall${samples === 1 ? 'y' : 'ies'} is below the point where an average is meaningful. The spread between individual Bear rallies is usually wider than the gap you would be reading into this.`,

    bestJoinTargets: 'Best join targets',
    bestJoinTargetsBlurb:
      "Rally leads whose rallies this player has produced the most damage in. This measures the pairing, not the lead's quality on their own.",
    rallyLead: 'Rally lead',
    noJoinTargets: 'No joins recorded against a named lead yet.',
    byCaptainHero: 'By captain hero',
    captainHero: 'Captain hero',
    noCaptainHero: "No captain hero has been recorded on this player's rallies yet.",
    formationComparison: 'Formation comparison',
    formationBlurb:
      "Normalized against this player's own baseline, so it compares formations to each other rather than to the alliance.",
    formation: 'Formation',
    noFormation: "No troop formation has been recorded on this player's rallies yet.",
    byWave: 'By wave',
    noWaves: 'No wave numbers have been recorded yet.',

    breakdownColumns: {
      rank: '#',
      mean: 'Mean',
      median: 'Median',
      best: 'Best',
      normalized: 'Normalized',
    },
    normalizedValue: (value: string) => `${value}×`,
    thinRows: (thin: number, total: number, threshold: number) =>
      `${thin} of ${total} row${total === 1 ? '' : 's'} rest${thin === 1 ? 's' : ''} on fewer than ${threshold} samples. Ordering between those rows is not reliable.`,

    trendsTitle: 'Recent trend',
    trendWindows: {
      last3: 'Last 3 sessions',
      last5: 'Last 5 sessions',
      last10: 'Last 10 sessions',
    },
    trendColumns: {
      window: 'Window',
      meanDamage: 'Mean damage',
      medianDamage: 'Median damage',
      changeVsPrevious: 'Change vs previous window',
      sessions: 'Sessions',
    },
    noEarlierWindow: 'no earlier window',
    trendsCaveat:
      "A change between windows reflects everything that varied between them — opponent trap level, who else showed up, wave position, and the player's own account. It is a description of what happened, not a measure of improvement.",
  },

  // ---------------------------------------------------------------------------
  // Upgrade Impact (spec section 27).
  //
  // The wording here is mandated, not stylistic: "associated with", "observed
  // after", "likely contributor", "insufficient controlled samples". Nothing in
  // this block may assert that a change produced a difference.
  // ---------------------------------------------------------------------------
  upgradeImpact: {
    title: 'Upgrade impact',
    blurb:
      'Damage observed before and after a recorded change. These are uncontrolled observations from live sessions, not experiments — read them as leads worth testing, not as measured effects. The Experiments screen is where a change gets held constant long enough to say more.',
    empty:
      "No changes have been recorded against this player's Bear history yet. Impacts appear once a hero, skill or gear change is logged with sessions on both sides of it.",
    recordedAt: (when: string) => `recorded ${when}`,
    notIsolated: 'not an isolated change',
    notIsolatedTitle: 'Other things changed in the same window.',
    medianBefore: 'Median before',
    medianAfter: 'Median after',
    differenceObserved: 'Difference observed after the change',
    notEnoughData: 'not enough data',
    alsoChanged: 'Also changed during the same window:',

    /** Confounded: at most a likely contributor, never the cause. */
    confounded: (description: string, others: number) =>
      `This difference is associated with the window in which "${description}" was recorded, but ${others} other change${others === 1 ? '' : 's'} occurred in the same window. On this evidence "${description}" is at most a likely contributor; the difference cannot be attributed to it. Isolating it would need an experiment holding the other variables constant.`,
    /** Too few samples on one side to distinguish from ordinary variation. */
    thin: (description: string, side: string) =>
      `Observed after "${description}" was recorded. There are insufficient controlled samples on ${side} to distinguish this from ordinary session-to-session variation. Treat it as a lead, not a result.`,
    beforeSide: 'the before side',
    afterSide: 'the after side',
    /** The strongest claim available: a likely contributor, still uncontrolled. */
    clean: (description: string) =>
      `Observed after "${description}" was recorded, with no other change logged in the same window. That makes it a likely contributor to the difference. It remains an uncontrolled observation: opponent, wave and joiners still varied between sessions.`,
  },

  // ---------------------------------------------------------------------------
  // Alliance Bear Planner (spec sections 30-34, 61)
  // ---------------------------------------------------------------------------
  planner: {
    title: 'Alliance Bear Planner',
    noCoreAllianceBody:
      'The planner works from the core alliance roster. Configure and sync the alliance first.',
    noRosterTitle: 'No roster synced',
    noRosterBody: 'The alliance roster has not been fetched yet, so there is nobody to plan with.',

    objective: 'Objective',
    modes: {
      MAX_ALLIANCE_DAMAGE: {
        label: 'Maximise alliance damage',
        blurb:
          'Puts the strongest observed leads in the earliest waves. Best for a trap you are trying to clear.',
      },
      BALANCED_REWARDS: {
        label: 'Balance rewards',
        blurb:
          'Spreads lead slots so more members get lead credit, at some cost to total damage.',
      },
      PERSONAL_SCORE_ASSIST: {
        label: 'Assist one player',
        blurb: 'Arranges waves and joins around a single member. Choose who below.',
      },
    },
    waves: 'Waves',
    ralliesPerWave: 'Rallies per wave',
    autoPlaceholder: 'auto',
    generating: 'Generating…',
    notPersisted:
      'This plan was not stored, so it has no audit trail and cannot be edited or recalled later. Whatever you act on, act on it from this screen before leaving.',

    change: 'Change',
    creating: 'Opening…',
    useIt: 'Open',

    filterRoster: 'Filter roster',
    leadDecision: 'Lead decision',
    leadDecisionAria: (name: string) => `Lead decision for ${name}`,
    planAround: 'Plan around',
    leadPreference: {
      AUTO: 'Planner decides',
      FORCE_LEAD: 'Lock as lead',
      DO_NOT_LEAD: 'Not leading today',
    },
    noFilterMatch: 'No members match that filter.',
  },

  /**
   * The Bear Trap DAY screen.
   *
   * The copy carries a distinction the product depends on: the exclusion
   * between BT1 and BT2 is a GAME RULE (a ~48h participation cooldown, with both
   * traps on one day), while the wave count and the lead count are this
   * alliance's own practice. Copy that blurs the two would turn a convention
   * the operator can change into a law they think they cannot.
   */
  bearDay: {
    subtitle:
      'Plan a Bear Trap day. Both traps run on the same date, and taking part in one starts a ~48h cooldown, so each member goes to BT1, to BT2, or to neither.',

    dayTitle: 'Bear Trap day',
    dayBlurb:
      'Pick the date. One day per alliance per date, carrying both traps — which is what makes the BT1/BT2 choice exclusive rather than merely recommended.',
    date: 'Date',
    openDay: 'Open this day',
    recentDays: 'Recent days',
    loadingDays: 'Loading Bear Trap days',
    loadingDay: 'Loading the day',
    noDaysYet: 'No Bear Trap days yet. Open one above.',
    ralliesEntered: 'Rallies entered',
    trapAssignedCount: (trapNumber: number, count: number) =>
      `BT${trapNumber}: ${count} assigned`,

    assignmentTitle: (bt1: number, bt2: number) => `Assignment — BT1 ${bt1} · BT2 ${bt2}`,
    assignmentBlurb:
      'Send each member to one trap or to neither. Nobody can take both: participating starts a ~48h cooldown and the two traps run the same day, so this is the game refusing rather than an alliance preference. Lock or exclude a lead where you have already decided — manual decisions outrank anything the planner recommends.',
    trapColumn: 'Trap',
    neither: 'Neither',
    trapChoiceAria: (name: string, choice: string) => `${name}: ${choice}`,
    splitEvenly: 'Split shown evenly',
    saveAssignment: 'Save assignment',
    saving: 'Saving…',
    unsavedChanges: 'Unsaved changes',
    savedAt: (bt1: number, bt2: number) => `Saved. BT1 ${bt1}, BT2 ${bt2}.`,
    unassignedCount: (count: number) => `${count} not taking part`,

    trapTitle: (trapNumber: number, count: number) =>
      `Bear Trap ${trapNumber} — ${count} assigned`,
    trapEmpty: (trapNumber: number) =>
      `Nobody is assigned to trap ${trapNumber} yet. Assign members above and save, then this trap can be sized and planned.`,
    loadingSizing: 'Working out how big to run this trap',
    enterRallies: 'Enter this trap’s rallies',

    recommendedLeads: 'Rally leads',
    totalLaunches: 'Rallies launched',
    /** Counted from rows, which is stronger evidence than a figure in a rule. */
    observedCounted: (count: number, detail: string) =>
      `Counted ${count} rally leads in ${detail}.`,
    observedQuoted: (count: number) =>
      `${count} rally leads is what the strategy rules record from play; no entered trap has been counted yet.`,
    sizingConfidence: (percent: number) =>
      `Confidence ${percent}% — lowered by every figure that had to be assumed rather than read.`,

    useRecommendation: 'Use the recommendation',
    decideMyself: 'Set the waves and rallies myself',
    blankMeansAuto: 'Leave a box empty to let the planner decide that one.',
    pickFocus: 'Choose the member to plan around',
    generateForTrap: (trapNumber: number) => `Generate the BT${trapNumber} plan`,
  },

  planResult: {
    wavePlan: 'Wave plan',
    rules: (version: string) => `rules ${version}`,
    noWaves: 'The planner produced no waves. Check the warnings below.',
    waveHeading: (wave: number) => `Wave ${wave}`,
    noRalliesInWave: 'No rallies assigned to this wave.',
    opensAt: (when: string) => `opens ${when}`,
    relativeStrength: (value: string) => `${value}× relative`,
    relativeStrengthTitle:
      'Relative to the other rallies in this plan. Not an absolute damage prediction.',
    joiners: 'Joiners:',
    noJoinerPreference:
      'No joiner preference — there is no recorded pairing history to base one on.',
    backupLead: (name: string) => `Backup lead: ${name}`,
    noBackupLead: 'No backup lead available.',
    whyThisLead: 'Why this lead',

    assumptionsTitle: 'Assumptions this plan rests on',
    noAssumptions:
      'The planner did not record any assumptions, which is itself worth questioning before acting on the plan.',

    leadRankingTitle: 'Lead ranking',
    leadRankingBlurb:
      "A Bear lead score only. It says nothing about a player's value in PvP, garrison or any other role — those are scored separately and a strong lead here is often not the strongest elsewhere.",
    noCandidates: 'No lead candidates were scored.',
    columns: {
      rank: '#',
      leadScore: 'Lead score',
      observedAvgDamage: 'Observed avg damage',
      wave: 'Wave',
      explain: 'Explain',
    },
    neverLed: 'never led',
    breakdown: 'Breakdown',

    planConfidence: 'Plan confidence',
    planConfidenceFoot: 'across all waves',
    storedAs: 'Stored as',
    recorded: 'recorded',
    notPersistedFoot: 'not persisted — this plan has no audit trail',
  },

  persistedPlan: {
    loading: 'Loading stored plan',
    title: 'Stored plan',
    manuallyEdited: 'manually edited',
    manuallyEditedTitle:
      "An operator edited this plan. What you are looking at is their version, not the engine's.",
    asGenerated: 'as generated',
    hideOriginal: 'Hide the original',
    compareOriginal: 'Compare with the original',
    stopEditing: 'Stop editing',
    editPlan: 'Edit this plan',
    model: 'Model',
    rules: 'Rules',
    generated: 'Generated',
    edited: 'Edited',
    editedBy: (who: string) => ` by ${who}`,
    reason: 'Reason',
    manualNotice:
      "This plan was changed by an operator after it was generated. Their version is what the alliance should run. The engine's original is kept for comparison and will not overwrite this — regenerating creates a new plan rather than replacing it.",
    effectiveOperator: "Effective plan (operator's version)",
    effective: 'Effective plan',
    comparisonNotice:
      'Below is what the engine produced before the edit. It is shown for comparison only.',
    originalAsGenerated: 'Original, as generated',
    legacyTitle: (label: string) => `${label} — written by an older plan format`,
    legacyBodyPrefix: 'This plan was generated by ',
    legacyBodySuffix:
      ', whose output no longer matches the current plan schema. It is kept intact rather than reinterpreted, because guessing how an old format maps onto the new one would invent detail the original never had.',
    legacyNotEditable:
      'This plan was written in an older format, so it cannot be edited in place. Generate a new plan instead — the old one stays in the record either way.',
    editBlurb:
      "Reassign rally leads. Your version is stored alongside the engine's, never over it, so a later recalculation cannot quietly undo a decision you made on purpose.",
    noRalliesInWave: 'No rallies in this wave.',
    rallyLabel: (index: number) => `Rally ${index}`,
    notePlaceholder: 'e.g. Damned is not online tonight — swapped in Sable',
    noteBlurb:
      'Recording why matters more than it looks: in three weeks this is the only thing that explains why the plan and the engine disagree.',
    saveMyVersion: 'Save my version',
    manualExplanation: 'Assigned manually by an operator, overriding the engine.',
  },

  // ---------------------------------------------------------------------------
  // Bear history and entry (spec sections 24, 35, 72)
  // ---------------------------------------------------------------------------
  bearHistory: {
    title: 'Bear Trap history',
    subtitle:
      'Record what actually happened. Every analysis in this app is built from these entries and nothing else.',
    noCoreAllianceBody:
      'Sessions belong to an alliance, so the alliance has to be configured and synced first.',
    session: 'Session',
    recordingInto: 'recording into',
    savedThisSitting: (count: number) =>
      `${count} rall${count === 1 ? 'y' : 'ies'} saved this sitting`,
    closeSession: 'Close session',
    trapNumber: 'Trap number',
    startSession: 'Start a session',
    starting: 'Starting…',
    orContinue: 'or continue an existing one',
    sessionIdPlaceholder: 'session id',
    existingSessionIdAria: 'Existing session id',
    continue: 'Continue',
    startToRecord: 'Start or continue a session to record rallies.',
    noRosterTitle: 'No roster synced',
    noRosterBody:
      'Rally members are picked from the alliance roster, which has not been fetched yet.',

    pastSessions: 'Past sessions',
    loadingSessions: 'Loading sessions',
    noSessionsTitle: 'No sessions recorded yet',
    noSessionsBody:
      'Every Bear analysis in this app is built from sessions entered here. Once the first one is recorded it appears in this log.',
    draftsNotice: (drafts: number) =>
      drafts === 1
        ? '1 session is still being entered. Its totals are partial sums, and any analysis drawn from it is working from incomplete data until entry is finished.'
        : `${drafts} sessions are still being entered. Their totals are partial sums, and any analysis drawn from them is working from incomplete data until entry is finished.`,
    columns: {
      session: 'Session',
      started: 'Started',
      entry: 'Entry',
      coverage: 'Coverage',
      rallies: 'Rallies',
      allianceDamage: 'Alliance damage',
    },
    noTrapNumber: 'no trap number',
    trapLabel: (trapNumber: number) => `Bear Trap #${trapNumber}`,
    partialTitle:
      'Entry is unfinished, so this total covers only what has been typed in so far.',
    partialSuffix: 'partial',
    continueEntry: 'Continue entry',
    enteringNow: 'entering now',
    continueEntryTitle: 'Reopen this session in the entry form above to keep recording.',
    showingRecent: (shown: number, total: string) =>
      `Showing the ${shown} most recent of ${total} recorded sessions.`,
  },

  entryProgress: {
    complete: 'complete',
    completeTitle: 'Entry was marked finished. Totals are the full recorded session.',
    stillBeingEntered: 'still being entered',
    stillBeingEnteredTitle:
      "Data entry is still in progress. Every total on this session is a partial sum and must not be read as the session's result.",
    turnoutNotEntered: 'turnout not entered',
    recordedOnly: (recorded: string) => `${recorded} recorded · `,
    turnoutNotEnteredTitle:
      "The game's participant count was not entered, so there is nothing to measure recorded entries against.",
    recordedOf: (recorded: string, expected: string) => `${recorded} of ${expected} recorded`,
    percentEntered: (percent: number) => `${percent}% entered`,
    percentEnteredTitle: (percent: number) =>
      `Only ${percent}% of the players who turned up have been entered. Damage totals for this session are sums over the entered rows only.`,
    draftNoCoverage:
      'This session is still being entered. Its totals are sums over whatever has been recorded so far, not the session result.',
    draftWithCoverage: (recorded: number, expected: number) =>
      `This session is still being entered — ${recorded} of ${expected} participants so far. Its totals are partial sums, not the session result.`,
    completeButThin: (recorded: number, expected: number) =>
      `Entry is marked complete, but only ${recorded} of ${expected} participants have rows. The totals below cover the recorded players only.`,
    noTurnoutRecorded:
      'The game’s participant count was not entered, so there is no way to tell whether every player who turned up has a row here.',
  },

  sessionDetail: {
    loading: 'Loading session',
    genericTitle: 'Bear session',
    started: 'Started',
    ended: 'Ended',
    allianceTotalDamage: 'Alliance total damage',
    asReportedByGame: 'as reported by the game',
    entryCoverage: 'Entry coverage',
    ralliesRecorded: 'Rallies recorded',
    notes: 'Notes',
    weakContext: (weak: number, total: number) =>
      `${weak} of ${total} recorded rows have a hero/gear snapshot that is stale or missing. Those rows record what damage was done, but not reliably by which loadout — do not read an upgrade's effect out of them.`,
    missingTroops: (missing: number, total: number) =>
      `${missing} of ${total} rows have no troop count. They are excluded from damage-per-troop figures rather than counted as zero.`,
    noRalliesTitle: 'No rallies recorded in this session',
    noRalliesBody: 'The session exists but nobody has entered a rally into it yet.',
    rally: 'Rally',
    waveLabel: (wave: number) => `Wave ${wave}`,
    hideJoiners: 'Hide joiners',
    showJoiners: (count: number) => `Show ${count} joiner${count === 1 ? '' : 's'}`,
    rallyDamage: 'Rally damage',
    troops: 'Troops',
    troopsOf: (actual: string, capacity: string | null) =>
      capacity ? `${actual} / ${capacity}` : actual,
    filled: (percent: number) => `${percent}% filled`,
    returnCycle: 'Return cycle',
    leadHeroes: 'Lead heroes',
    noJoinersRecorded: 'No joiners were recorded for this rally.',
    columns: {
      order: '#',
      captainHero: 'Captain hero',
      infantry: 'Infantry',
      cavalry: 'Cavalry',
      archer: 'Archer',
      totalTroops: 'Total troops',
      damage: 'Damage',
      loadoutContext: 'Loadout context',
      loadoutContextTitle: 'How close the bound hero/gear snapshot sits to this rally.',
    },
    /** Blank means the operator could not see the number, not that it was zero. */
    troopNotRecorded: 'not recorded',
    troopNotRecordedTitle:
      'Not recorded. Excluded from per-troop figures rather than counted as zero.',
    notAssessed: 'not assessed',
    notAssessedTitle: 'The server did not report how the loadout snapshot was bound.',
    contextQuality: {
      EXACT: 'exact',
      NEAR: 'near',
      STALE: 'stale',
      MISSING: 'missing',
    },
    contextExplanation: {
      EXACT:
        'The bound hero/gear snapshot is within about half an hour of this rally, so the loadout attributed to this row is very likely the one actually used.',
      NEAR: 'The bound snapshot is within a few hours. Probably the right loadout, but a change in between would not be visible.',
      STALE:
        'The bound snapshot is hours away from this rally. It cannot support any claim about which gear or skills produced this damage.',
      MISSING:
        'No hero/gear snapshot could be bound to this row at all. Nothing about this player’s loadout at the time is known.',
    },
    /** @param agePhrase Already carries its direction, e.g. "3h 20m before". */
    contextAge: (explanation: string, agePhrase: string) =>
      `${explanation} The bound snapshot was taken ${agePhrase} this rally.`,
    before: 'before',
    after: 'after',
  },

  rallyEntry: {
    title: 'Record a rally',
    joinersEntered: (count: number) => `${count} joiner${count === 1 ? '' : 's'} entered`,
    rallyLead: 'Rally lead',
    wave: 'Wave',
    order: 'Order',
    rallyCapacity: 'Rally capacity',
    actualTroops: 'Actual troops',
    totalRallyDamage: 'Total rally damage',
    leadHeroIds: 'Lead hero ids',
    leadHeroIdsPlaceholder: 'e.g. 104 201 307',
    columns: {
      row: '#',
      joiner: 'Joiner',
      joinOrder: 'Join order',
      captainHero: 'Captain hero',
      infantry: 'Infantry',
      cavalry: 'Cavalry',
      archer: 'Archer',
      totalTroops: 'Total troops',
      damage: 'Damage',
    },
    joinerAria: (row: number) => `Joiner for row ${row}`,
    cellAria: (field: string, row: number) => `${field} for row ${row}`,
    removeRowAria: (row: number) => `Remove row ${row}`,
    removeRowTitle: 'Remove row',
    /** The rule the whole Bear analysis rests on: blank stays blank. */
    legendLead: 'Enter adds a row. Tab moves across. A blank cell is submitted as',
    legendNotRecorded: 'not recorded',
    legendTail:
      '— it is never turned into a zero, so a joiner whose troop split you could not see will not drag down the damage-per-troop numbers. You can paste tab-separated rows from a spreadsheet into any cell.',
    saveRally: 'Save rally',
    add5Rows: 'Add 5 rows',
    rallySaved: 'Rally saved.',
    savedJoiners: (count: number, wave: number | null) =>
      `Saved ${count} joiner${count === 1 ? '' : 's'}${wave === null ? '' : ` in wave ${wave}`}.`,
    weakBindings: (weak: number, total: number) =>
      `${weak} of ${total} rows could not be tied to a recent hero/gear snapshot. Their damage is recorded, but the loadout behind it is not established — those rows cannot support an upgrade comparison later.`,
  },

  buildRally: {
    needsLead: 'A rally needs a lead. Everything else on this form is optional.',
    fields: {
      wave: 'wave',
      order: 'order',
      rallyCapacity: 'rally capacity',
      actualTroops: 'actual troops',
      totalRallyDamage: 'total rally damage',
      leadHeroIds: 'lead hero ids',
      leadHeroIdsMax: 'lead hero ids (at most three)',
      joinOrder: 'join order',
      captainHero: 'captain hero',
      infantry: 'infantry',
      cavalry: 'cavalry',
      archer: 'archer',
      totalTroops: 'total troops',
      damage: 'damage',
    },
    rowField: (row: number, field: string) => `row ${row} ${field}`,
    invalidFields: (fields: string) =>
      `These need to be whole numbers or left blank: ${fields}. Leaving a value blank is fine and is recorded as "not known" — it is not recorded as zero.`,
    duplicatePlayers: (players: string) =>
      `The same player appears more than once in this rally: ${players}.`,
  },

  // ---------------------------------------------------------------------------
  // Rally Lab (spec sections 36, 37)
  // ---------------------------------------------------------------------------
  rallyLab: {
    title: 'Rally Lab',
    subtitle:
      'Compose a rally for one role, from what this alliance has actually been observed doing.',
    noCoreAllianceBody:
      'The lab composes rallies from the alliance roster, which has not been configured or synced yet.',
    perRoleNotice:
      "Scores are per role. A recommendation here says nothing about a player's value in any other operation — a strong Bear lead is frequently not the strongest garrison, and this system keeps those judgements separate rather than averaging them into one number.",
    whatAreYouBuilding: 'What are you building',
    operation: 'Operation',
    side: 'Side',
    attack: 'Attack',
    defense: 'Defense',
    joiners: 'Joiners',
    leadThisRally: 'Lead this rally',
    letModelChoose: 'Let the model choose',
    noRosterTitle: 'No roster synced',
    noRosterBody:
      'There is nobody to compose a rally from until the alliance roster has been fetched.',
    availableTitle: (selected: number) => `Available for this rally — ${selected} selected`,
    recommend: 'Recommend a rally',
    composing: 'Composing…',
    pickAvailableFirst: 'Pick who is available first.',

    noRoleTitle: 'That side does not exist for this operation',
    noRoleBody:
      'The model has no role to score against for this combination, so it declined to answer rather than returning a generic ranking.',
    recommendedFor: (role: string) => `Recommended rally — ${role}`,
    lead: 'Lead',
    backupLead: 'Backup lead',
    noLead: 'No lead could be recommended from the players you selected.',
    noBackup: 'No backup available from this roster.',
    leadHeroes: 'Lead heroes',
    noHeroRecommendation: 'No hero recommendation — nothing recorded supports one.',
    rawHeroId: (heroId: number | string) => `hero #${heroId}`,
    computedFrom: (when: string) => `Computed from data as of ${when}`,
    rules: (version: string) => ` · rules ${version}`,
    run: (runId: string) => ` · run ${runId}`,
    notPersisted: ' · not persisted',
    joinersTitle: 'Joiners',
    noJoiners: 'No joiners were recommended from the players you selected.',
    joinerColumns: {
      rank: '#',
      score: 'Score',
      captainHero: 'Captain hero',
    },
    noRecommendation: 'no recommendation',

    troopComposition: 'Troop composition',
    /**
     * The interesting case, and the default one early on. There is a widely
     * repeated community ratio; printing it because we had nothing else would
     * turn a rumour into this system's recommendation.
     */
    noComposition: 'No composition recommended',
    noCompositionBody:
      'No recorded result supports a troop split for this role yet. There is a widely repeated community ratio, but nobody here has measured it, so this system will not present it as a recommendation. Record a few rallies with troop counts and a comparison becomes possible.',
    normalized: (value: string) => `${value}× normalized`,
    normalizedTitle: "Relative to this alliance's own baseline for the role.",

    missingTitle: 'Who could not be ranked',
    missingBlurb:
      'These players were left out of the ranking rather than given a guessed score. A player missing here is a gap in our records, not a judgement about them.',
  },

  // ---------------------------------------------------------------------------
  // Events (spec sections 19-21, 38-45, 57)
  // ---------------------------------------------------------------------------
  events: {
    plannerNotAutomated: 'Planning is not automated for this operation yet.',
    plannerLands: (phase: string) =>
      ` That lands with ${phase}. Scheduling and rostering below are live — nothing on this page is a placeholder.`,
    newEvent: (title: string) => `New ${title}`,
    name: 'Name',
    starts: 'Starts',
    schedule: 'Schedule',
    scheduling: 'Scheduling…',
    optionalFieldsNote:
      'Both fields are optional — an operation with no start time is still worth recording, and a blank start reads as "not scheduled yet" rather than as midnight.',
    scheduled: 'Scheduled',
    loadingEvents: 'Loading events',
    nothingScheduledTitle: 'Nothing scheduled yet',
    nothingScheduledBody:
      'Operations are created by an R4 — they are never inferred from the game. Schedule one above and it appears here.',
    columns: {
      name: 'Name',
      starts: 'Starts',
      status: 'Status',
      created: 'Created',
    },
    notScheduled: 'not scheduled',
    roster: 'Roster',
    cancelTitle: 'Marks this operation cancelled. It stays in the record — nothing is deleted.',
    cancelNote:
      'Cancelling keeps the operation in the record with a cancelled status. Nothing here is ever deleted.',

    loadingRoster: 'Loading roster',
    rosterFor: 'Roster —',
    cancelledNotice:
      'This operation was cancelled. It is kept so the record of what was planned stays intact; the roster below is what it had at the time.',
    /** Staffing information, never a ranking (spec section 50). */
    rosterBlurb:
      'Who is expected. This is staffing information — it exists so an operation can be filled, not to rank members.',
    loadingAllianceRoster: 'Loading alliance roster',
    noAllianceRosterTitle: 'No alliance roster synced',
    noAllianceRosterBody:
      'Participants are picked from the alliance roster, which has not been fetched yet.',
    saveParticipants: (count: number) => `Save ${count} participants`,
    selectAtLeastOne: 'Select at least one player — the API will not accept an empty roster.',
    savedCount: (count: number) => `Saved ${count}.`,
    recordedParticipants: 'Recorded participants',
    participantColumns: {
      player: 'Player',
      role: 'Role',
      team: 'Team',
      attendance: 'Attendance',
      notes: 'Notes',
    },
    unassigned: 'unassigned',

    championship: {
      title: 'Alliance Championship',
      subtitle: 'Schedule rounds, record who is available, and assign lanes.',
      plannerNote:
        'Lane assignment and win projections are not built. When they arrive the probabilities will be heuristic at first and will be labelled as such with their assumptions listed — a projected 68% from a model with no recorded rounds behind it is a guess wearing a percentage sign.',
      plannerPhase: 'the AC lane optimizer and opponent pattern history (spec sections 40-42)',
    },
    swordland: {
      title: 'Swordland',
      subtitle: 'Schedule the event and record the roster.',
      plannerNote:
        'Legion building is not built. Legion size limits and role rules are unverified game mechanics, so they are stored as UNKNOWN strategy rules rather than hardcoded, and the builder will not produce a roster that silently depends on a limit nobody has confirmed.',
      plannerPhase: 'Swordland rosters (spec section 43)',
    },
    fortress: {
      title: 'Sanctuary / Fortress',
      subtitle: 'Schedule an operation and record who is attacking or garrisoning.',
      plannerNote:
        "Attack ordering, stagger offsets and garrison assignment are not built. Timing offsets will come from configurable rules or from this alliance's own recorded history — never from a universal constant, because nobody has verified one exists.",
      plannerPhase: 'structure operations (spec section 38)',
    },
    castle: {
      title: 'Castle',
      subtitle: 'Schedule castle operations and record the roster.',
      plannerNote: 'Attack ordering and garrison staffing recommendations are not built.',
      plannerPhase: 'castle operations (spec section 39)',
    },
    viking: {
      title: 'Viking Vengeance',
      subtitle: 'Schedule the event and record who is taking part.',
      plannerNote:
        'Pairing and support assignment are not built. When they are, they will be drawn from recorded results rather than from power.',
      plannerPhase: 'Viking Vengeance support (spec section 44)',
    },
    triAlliance: {
      title: 'Tri-Alliance Clash',
      subtitle: 'Schedule the clash and record our side of the roster.',
      plannerNote:
        "Cross-alliance coordination is not built. We can only see our own alliance's data, so any plan spanning the other two would be built on gaps.",
      plannerPhase: 'a later phase (spec section 57)',
    },
    mobilization: {
      title: 'Mobilization / Brawl',
      subtitle: 'Schedule the window and record who is taking part.',
      plannerNote:
        'Point contribution and task coverage tracking are not built. Participation here is for staffing and planning; it is not a ranking of members and will not become one.',
      plannerPhase: 'mobilization tracking (spec section 45)',
    },
    experiments: {
      title: 'Bear experiments',
      subtitle: 'Holding one variable constant long enough to learn something.',
      notice:
        'The Upgrade Impact panel on the Personal Analyzer can only ever report associations, because several things change between two live sessions. Experiments exist to fix that: pick one variable, hold the rest, run it across enough sessions, and get a result worth more than a hunch.',
      feature: 'Experiments',
      phase: 'Phase 4 (spec section 28), which is out of scope for the current build',
      describes:
        'Define a hypothesis, mark which sessions belong to it, and read the outcome with its sample count and confidence.',
    },
  },

  // ---------------------------------------------------------------------------
  // State Intel (spec sections 46-49, 57)
  // ---------------------------------------------------------------------------
  stateIntel: {
    noKingdomTitle: 'No kingdom configured',
    noKingdomBody:
      'State intelligence is scoped to the configured kingdom. Set and sync it server-side first.',

    alliances: {
      title: 'State alliances',
      subtitle: (kingdomId: number) => `Alliances observed in kingdom ${kingdomId}.`,
      loading: 'Loading alliances',
      emptyTitle: 'No alliances synced',
      emptyBody: 'The kingdom alliance list has not been fetched yet.',
      ours: 'ours',
      columns: {
        alliance: 'Alliance',
        level: 'Level',
        power: 'Power',
        kills: 'Kills',
        members: 'Members',
        leader: 'Leader',
        age: 'Age',
      },
    },

    players: {
      title: 'State players',
      subtitle: 'Every player this system has ever stored, across all alliances in the kingdom.',
      searchPlaceholder: 'Search by name',
      searchAria: 'Search players',
      loading: 'Loading players',
      noMatchTitle: 'No player matches that search',
      noMatchBody: 'Only players who have been synced at least once are searchable here.',
      emptyTitle: 'No players stored yet',
      emptyBody: 'Players are stored as kingdom and alliance syncs discover them.',
      showing: (shown: number, total: string) => `Showing ${shown} of ${total} stored players.`,
      noMatch: 'No player matches.',
    },

    transfers: {
      title: 'Transfers',
      subtitle: 'Alliance movement detected across the kingdom.',
      loading: 'Loading transfers',
      emptyTitle: 'No transfers detected in this window',
      emptyBody:
        'Transfers are found by comparing roster snapshots. A quiet window is a normal result, not a failure.',
    },

    leaderboards: {
      title: 'Leaderboards',
      subtitle: 'Kingdom leaderboard snapshots.',
      feature: 'Leaderboards',
      phase: 'the phase that adds leaderboard storage (spec section 10.3)',
      describes:
        'Leaderboard boards refresh on roughly a daily clock upstream, so this screen will show each board with its own age rather than a shared timestamp.',
    },

    kvk: {
      title: 'KvK',
      subtitle: 'Kingdom versus Kingdom standings and matchups.',
      feature: 'KvK',
      phase: 'the phase that adds KvK ingestion (spec section 11)',
      describes:
        'Matchup, standings and per-kingdom contribution, once the KvK endpoint is being polled and stored.',
    },
  },

  // ---------------------------------------------------------------------------
  // Data section (spec sections 57, 64, 68)
  // ---------------------------------------------------------------------------
  data: {
    sync: {
      title: 'Sync status',
      subtitle: 'What the background jobs have and have not managed to fetch.',
      loading: 'Loading sync status',
      budgetCritical: (percent: number) =>
        `${percent}% of today's upstream request budget is spent. Syncs will start being skipped, which means the freshness figures elsewhere in the app will quietly get worse rather than showing an error.`,
      rateLimited: (count: number) =>
        `${count} request${count === 1 ? ' was' : 's were'} rate limited today. Affected resources are older than their schedule suggests.`,
      requestsUsed: 'Requests used today',
      requestsUsedValue: (used: string, budget: string) => `${used} / ${budget}`,
      budgetFoot: (percent: number) => `${percent}% of budget`,
      rateLimitedToday: 'Rate limited today',
      queueDepth: 'Queue depth',
      lastKingdomSync: 'Last kingdom sync',
      lastAllianceSync: 'Last alliance sync',
      ago: (duration: string) => `${duration} ago`,
      neverRun: 'Never run',
      failuresTitle: 'Failures in the last 24 hours',
      noFailures:
        'Nothing has failed in the last 24 hours. Note that a job which never ran also produces no failure — check the last-sync times above.',
      failureColumns: {
        finished: 'Finished',
        job: 'Job',
        resource: 'Resource',
        kind: 'Kind',
        message: 'Message',
      },
      unknownKind: 'unknown',
      noMessage: 'no message',
      staleTitle: 'Stale players',
      staleBlurb:
        'Players whose profile has not refreshed within its expected interval. Any analysis involving them is working from older inputs than the rest of the roster.',
      nothingBehind: 'Nothing is behind schedule.',
      staleColumns: {
        player: 'Player',
        age: 'Age',
      },
    },

    freshness: {
      title: 'Freshness',
      subtitle: 'How old each source is, by its own clock.',
      /** Spec section 64: never one global timestamp. */
      notice:
        'There is deliberately no single "last updated" anywhere in this app. A player profile refreshes upstream roughly every 10 minutes, kills and standings move on a ~6 hour ranking-board clock, and leaderboards on roughly a day. One combined timestamp would be accurate for none of them.',
      loading: 'Loading freshness',
      kingdom: 'Kingdom',
      allianceRoster: 'Alliance roster',
      neverFetched: 'Never fetched',
      perPlayerTitle: 'Per-player freshness',
      perPlayerBlurb:
        'Each player carries four independent clocks — profile, kills, standings and hero gear — shown in the header of their detail page. Only players currently behind schedule are listed here.',
      nonePending: 'No player is behind its refresh schedule.',
      playerAge: (duration: string) => `— ${duration} old`,
    },

    rules: {
      title: 'Strategy rules',
      subtitle: 'Game mechanics this system does not claim to know.',
      notice:
        'Uncertain mechanics — joiner skill selection, optimal troop ratios, hero star encoding, equipment id encoding, rally stagger offsets — are stored as rules with an explicit UNKNOWN status rather than hardcoded. This screen is where they get reviewed and, when the alliance establishes one empirically, promoted.',
      feature: 'Rule editing',
      phase: 'the phase that lets rules be edited from this screen (spec section 22)',
      describes:
        'Each rule with its current status, the evidence behind it, and who last changed it.',
      loading: 'Loading strategy rules',
      noRulesTitle: 'No strategy rules',
      noRulesBody: 'Nothing has been recorded yet. Rules arrive through migrations.',
      active: 'in use',
      superseded: 'superseded',
      verified: 'official text',
      unscored: 'unscored',
      confidenceLabel: (c: number) => `confidence ${c.toFixed(2)}`,
      confidenceTitle:
        'How likely this is true for Kingshot specifically. 0.8+ official or corroborated; 0.6-0.8 two independent sources; 0.4-0.6 one source or several sharing an origin; below 0.4 weak or contested.',
      showConfig: 'Show the stored values',
      source: 'Source',
      noSource: 'no source recorded',
      effectiveFrom: 'Effective from',
    },

    catalog: {
      title: 'Catalog',
      subtitle: 'Hero, skill and equipment ids, and what we have confirmed about them.',
      feature: 'Catalog browsing',
      phase: 'the phase that exposes the catalog tables over the API (spec section 9)',
      describes:
        'Until then, unrecognised ids are shown throughout the app as raw ids marked "unverified" rather than being given a guessed name — see any player\'s Heroes tab.',
    },

    settings: {
      title: 'Settings',
      subtitle: 'What this browser session is connected to.',
      connection: 'Connection',
      apiBaseUrl: 'API base URL',
      supabaseProject: 'Supabase project',
      signedInAs: 'Signed in as',
      notSignedIn: 'not signed in',
      kingdom: 'Kingdom',
      coreAlliance: 'Core alliance',
      notConfigured: 'not configured',
      language: 'Language',
      whereConfigTitle: 'Where configuration lives',
      whereConfigBody:
        'Kingdom, core alliance, sync tiers and API budget are server-side settings, not browser preferences. They are deliberately not editable from here: this bundle is public, and anything it could change it could also leak.',
      tokenNote:
        'The upstream API token exists only in Worker secrets. This app has never held it and has no code path that could request one.',
      notEditableTitle: 'Workspace settings are not editable yet',
      notEditableBody:
        "Editing kingdom, core alliance and sync configuration needs an authenticated settings API that does not exist yet. For now these are changed in the Worker's configuration.",
    },
  },

  notFound: {
    title: 'Not found',
    emptyTitle: 'No such screen',
    emptyBody: 'That route does not exist in this app.',
    back: 'Back to Overview',
  },
};
